import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { manifestReceivingAPI } from '../services/api';
import { toast } from 'react-toastify';

const ManifestReceivingModal = ({ isOpen, onClose, manifestData, requestId }) => {
  const queryClient = useQueryClient();
  const [selectedLines, setSelectedLines] = useState({});
  const [receivingData, setReceivingData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const processReceiptMutation = useMutation(
    (receiptData) => manifestReceivingAPI.processManifestReceipt(receiptData.manifestId, receiptData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['manifest-for-request', requestId]);
        queryClient.invalidateQueries(['request', requestId]);
        toast.success('Receipt processed successfully');
        onClose();
      },
      onError: (error) => {
        toast.error('Failed to process receipt: ' + error.message);
      },
      onSettled: () => {
        setIsSubmitting(false);
      }
    }
  );

  if (!isOpen || !manifestData) return null;

  const { manifest, lineItems = [] } = manifestData;

  // Filter line items that can still be received
  const receivableLines = lineItems.filter(item => 
    (item.qty_pending || 0) > 0 || (item.qty_expected - (item.qty_received || 0)) > 0
  );

  const handleLineSelection = (lineId, selected) => {
    setSelectedLines(prev => ({
      ...prev,
      [lineId]: selected
    }));

    if (!selected) {
      // Clear receiving data for unselected line
      setReceivingData(prev => {
        const newData = { ...prev };
        delete newData[lineId];
        return newData;
      });
    } else {
      // Initialize receiving data for selected line
      const line = receivableLines.find(l => l.id === lineId);
      setReceivingData(prev => ({
        ...prev,
        [lineId]: {
          quantity: Math.min(line.qty_pending || line.qty_expected - (line.qty_received || 0), 1),
          packaging_type: 'pallet',
          weight_lbs: '',
          length: '',
          width: '',
          height: '',
          pallet_label: '',
          notes: ''
        }
      }));
    }
  };

  const handleReceivingDataChange = (lineId, field, value) => {
    setReceivingData(prev => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        [field]: value
      }
    }));
  };

  const handleSubmit = () => {
    const selectedLineIds = Object.keys(selectedLines).filter(id => selectedLines[id]);
    
    if (selectedLineIds.length === 0) {
      toast.error('Please select at least one line item to receive');
      return;
    }

    // Validate required fields
    for (const lineId of selectedLineIds) {
      const data = receivingData[lineId];
      if (!data || !data.quantity || data.quantity <= 0) {
        toast.error('Please enter a valid quantity for all selected items');
        return;
      }
    }

    const receipts = selectedLineIds.map(lineId => ({
      line_item_id: lineId,
      quantity: parseInt(receivingData[lineId].quantity),
      packaging_type: receivingData[lineId].packaging_type,
      weight_lbs: receivingData[lineId].weight_lbs ? parseFloat(receivingData[lineId].weight_lbs) : null,
      length: receivingData[lineId].length ? parseFloat(receivingData[lineId].length) : null,
      width: receivingData[lineId].width ? parseFloat(receivingData[lineId].width) : null,
      height: receivingData[lineId].height ? parseFloat(receivingData[lineId].height) : null,
      pallet_label: receivingData[lineId].pallet_label || null,
      notes: receivingData[lineId].notes || null
    }));

    setIsSubmitting(true);
    processReceiptMutation.mutate({ manifestId: manifest.id, receipts });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="w-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Receive Manifest Items
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900">Manifest: {manifest.title || manifest.id}</h4>
                  <p className="text-sm text-blue-700">Vendor: {manifest.vendor || 'N/A'}</p>
                  <p className="text-sm text-blue-700">Status: {manifest.status}</p>
                </div>

                {receivableLines.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">All items in this manifest have been fully received.</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {receivableLines.map(line => {
                      const isSelected = selectedLines[line.id] || false;
                      const maxQuantity = line.qty_pending || (line.qty_expected - (line.qty_received || 0));
                      
                      return (
                        <div key={line.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handleLineSelection(line.id, e.target.checked)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-gray-900">{line.sku}</p>
                                <p className="text-sm text-gray-500">{line.description || 'No description'}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-900">Available: {maxQuantity}</p>
                              <p className="text-xs text-gray-500">
                                {line.qty_received || 0} / {line.qty_expected} received
                              </p>
                            </div>
                          </div>

                          {isSelected && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Quantity to Receive *</label>
                                <input
                                  type="number"
                                  min="1"
                                  max={maxQuantity}
                                  value={receivingData[line.id]?.quantity || ''}
                                  onChange={(e) => handleReceivingDataChange(line.id, 'quantity', e.target.value)}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700">Packaging Type *</label>
                                <select
                                  value={receivingData[line.id]?.packaging_type || 'pallet'}
                                  onChange={(e) => handleReceivingDataChange(line.id, 'packaging_type', e.target.value)}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                  <option value="pallet">Pallet</option>
                                  <option value="carton">Carton</option>
                                  <option value="loose">Loose</option>
                                </select>
                              </div>

                              {(receivingData[line.id]?.packaging_type === 'pallet' || receivingData[line.id]?.packaging_type === 'carton') && (
                                <>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">Weight (lbs)</label>
                                    <input
                                      type="number"
                                      step="0.1"
                                      value={receivingData[line.id]?.weight_lbs || ''}
                                      onChange={(e) => handleReceivingDataChange(line.id, 'weight_lbs', e.target.value)}
                                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">Pallet/Carton Label</label>
                                    <input
                                      type="text"
                                      value={receivingData[line.id]?.pallet_label || ''}
                                      onChange={(e) => handleReceivingDataChange(line.id, 'pallet_label', e.target.value)}
                                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                      placeholder="Auto-generated if empty"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">Length (inches)</label>
                                    <input
                                      type="number"
                                      step="0.1"
                                      value={receivingData[line.id]?.length || ''}
                                      onChange={(e) => handleReceivingDataChange(line.id, 'length', e.target.value)}
                                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">Width (inches)</label>
                                    <input
                                      type="number"
                                      step="0.1"
                                      value={receivingData[line.id]?.width || ''}
                                      onChange={(e) => handleReceivingDataChange(line.id, 'width', e.target.value)}
                                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">Height (inches)</label>
                                    <input
                                      type="number"
                                      step="0.1"
                                      value={receivingData[line.id]?.height || ''}
                                      onChange={(e) => handleReceivingDataChange(line.id, 'height', e.target.value)}
                                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                  </div>
                                </>
                              )}

                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Notes</label>
                                <textarea
                                  rows={2}
                                  value={receivingData[line.id]?.notes || ''}
                                  onChange={(e) => handleReceivingDataChange(line.id, 'notes', e.target.value)}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                  placeholder="Optional receiving notes..."
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {receivableLines.length > 0 && (
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || Object.values(selectedLines).every(selected => !selected)}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                  isSubmitting || Object.values(selectedLines).every(selected => !selected)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                }`}
              >
                {isSubmitting ? 'Processing...' : 'Process Receipt'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManifestReceivingModal;
